import * as THREE from 'three';
import type { Object3D } from 'three';
import {
	isSketchArcObject,
	isSketchCircleObject,
	isSketchEllipseObject,
	isSketchLineObject,
	isSketchParabolaObject,
	isSketchSlotObject,
	isSketchSplineObject,
	isSketchStyleSplineObject,
} from '../../../../mesh/guards';
import type { SketchArcObject } from '../../../../mesh/sketch/SketchArcObject';
import type { SketchCircleObject } from '../../../../mesh/sketch/SketchCircleObject';
import type { SketchEllipseDefinition, SketchEllipseObject } from '../../../../mesh/sketch/SketchEllipseObject';
import type { SketchLineObject } from '../../../../mesh/sketch/SketchLineObject';
import type { SketchParabolaObject } from '../../../../mesh/sketch/SketchParabolaObject';
import type { SketchSplineObject, SketchBezierCurveMetadata } from '../../../../mesh/sketch/SketchSplineObject';
import type { SketchStyleSplineObject } from '../../../../mesh/sketch/SketchStyleSplineObject';
import type { FeatureLine } from '../../../../mesh/feature/FeatureLine';
import type {
	ExternalGeometrySourceDescriptor,
	LineGeomFingerprint,
	TopologyEdgeMatchHints,
	TopologyEdgeSourceDescriptor,
} from '../../model/ExternalGeometrySourceDescriptor';
import {
	resolveExternalGeometrySourceRefEntityId,
	resolveExternalGeometrySourceDescriptorKey,
	resolveTopologyEndpointAnchorId,
} from '../../model/ExternalGeometrySourceDescriptor';
import {
	createExecutionDiagnostic,
	type ExecutionDiagnostic,
} from '../../model/SketchEntityReferenceDiagnostics';
import type { WorldCoordinate } from '../../model/ProjectionResult';
import { resolveSketchSlotEdgeByIndex } from '../../relation/resolveSketchSlotEdgeByIndex';
import {
	TopologyEdgeRelocationService,
	type TopologyEdgeRelocationCandidate,
} from '../topology/TopologyEdgeRelocationService';
import type {
	TopologyOwnerRuntimeResolver,
} from '../../../../../types/reference/sketch-entity-reference/TopologyOwnerRuntimeResolver';
import type {
	TopologySelectionRuntimeSourceSnapshot,
} from '../../../../../types/reference/sketch-entity-reference/TopologySelectionRuntimeSourceSnapshot';
import {TopologySampledCurveFittingService} from './fitting/TopologySampledCurveFittingService';
import {
	TopologySelectionRuntimeSourceResolver,
} from './TopologySelectionRuntimeSourceResolver';
import {
	SketchSplineReferenceEvaluationDefinitionResolver,
} from './SketchSplineReferenceEvaluationDefinitionResolver';
import type {
	ResolvedReferenceSourceEndpointAnchorIds,
	ResolvedLineReferenceSource,
	ResolvedCircleReferenceSource,
	ResolvedArcReferenceSource,
	ResolvedEllipseReferenceSource,
	ResolvedEllipseArcReferenceSource,
	ResolvedParabolaReferenceSource,
	ResolvedSplineReferenceSource,
	ResolvedStyleSplineReferenceSource,
	ResolvedSketchReferenceSource,
	SketchReferenceSourceResolverRuntime,
	SketchReferenceSourceResolverDependencies,
	SketchReferenceSourceResolveInput,
	SketchReferenceSourceResolverResult,
} from '../../../../../types/reference/sketch-entity-reference/SketchReferenceSourceResolver';

export type {
	ResolvedReferenceSourceEndpointAnchorIds,
	ResolvedLineReferenceSource,
	ResolvedCircleReferenceSource,
	ResolvedArcReferenceSource,
	ResolvedEllipseReferenceSource,
	ResolvedEllipseArcReferenceSource,
	ResolvedParabolaReferenceSource,
	ResolvedSplineReferenceSource,
	ResolvedStyleSplineReferenceSource,
	ResolvedSketchReferenceSource,
	SketchReferenceSourceResolverRuntime,
	SketchReferenceSourceResolverDependencies,
	SketchReferenceSourceResolveInput,
	SketchReferenceSourceResolverResult,
} from '../../../../../types/reference/sketch-entity-reference/SketchReferenceSourceResolver';

const GEOMETRY_EPSILON = 1e-6;
const FULL_ELLIPSE_EPSILON = 1e-6;
const FULL_ELLIPSE_ANGLE = Math.PI * 2;

/**
 * 转为固定长度世界坐标元组。
 *
 * @param vector 三维向量。
 * @returns 元组形式的世界坐标。
 */
function toWorldCoordinate(vector: THREE.Vector3): WorldCoordinate {
	return [vector.x, vector.y, vector.z];
}

/**
 * 比较两条线段指纹是否一致。
 * 说明：
 * 1. 允许起终点顺序反向；
 * 2. 使用固定公差，避免渲染层浮点抖动导致误判；
 * 3. 这里只用于运行时 preview / 解析闭合，不承担持久化匹配职责。
 *
 * @param fingerprint 记录在 descriptor 中的指纹。
 * @param startWorld 当前运行时起点。
 * @param endWorld 当前运行时终点。
 * @returns 一致时返回 true。
 */
function isMatchingLineFingerprint(
	fingerprint: LineGeomFingerprint,
	startWorld: THREE.Vector3,
	endWorld: THREE.Vector3,
): boolean {
	const fingerprintStart = new THREE.Vector3(...fingerprint.startWorld);
	const fingerprintEnd = new THREE.Vector3(...fingerprint.endWorld);
	const isForwardMatch = fingerprintStart.distanceTo(startWorld) <= GEOMETRY_EPSILON
		&& fingerprintEnd.distanceTo(endWorld) <= GEOMETRY_EPSILON;
	if (isForwardMatch) {
		return true;
	}

	return fingerprintStart.distanceTo(endWorld) <= GEOMETRY_EPSILON
		&& fingerprintEnd.distanceTo(startWorld) <= GEOMETRY_EPSILON;
}

/**
 * 构造对“descriptor 信息不足”的显式诊断。
 *
 * @param descriptor 来源描述。
 * @param message 诊断消息。
 * @returns 规范化诊断记录。
 */
function createDescriptorInsufficientDiagnostic(
	descriptor: ExternalGeometrySourceDescriptor,
	message: string,
): ExecutionDiagnostic {
	return createExecutionDiagnostic({
		kind: 'unsupported-source',
		level: 'error',
		message,
		sourceRefEntityId: resolveExternalGeometrySourceRefEntityId(descriptor),
	});
}

/**
 * 去重并过滤空字符串。
 *
 * @param values 候选字符串集合。
 * @returns 仅保留非空唯一字符串后的集合。
 */
function compactUniqueStrings(values: Array<string | undefined>): string[] {
	return values.filter((value, index, list): value is string => (
		typeof value === 'string'
		&& value.length > 0
		&& list.indexOf(value) === index
	));
}

/**
 * 判断椭圆定义是否表达完整椭圆。
 * 说明：
 * 1. 当前 `SketchEllipseObject` 同时承接完整椭圆与部分椭圆；
 * 2. `start=end` 与 `0 -> 2π` 都按完整椭圆处理，对齐对象自身 `canCreateContour` 语义；
 * 3. 该判定只影响 sourceKind，不修改源对象定义。
 *
 * @param definition 椭圆定义。
 * @returns 完整椭圆返回 true，部分椭圆返回 false。
 */
function isFullEllipseDefinition(definition: Required<SketchEllipseDefinition>): boolean {
	const angleSpan = Math.abs(definition.endAngle - definition.startAngle);
	const normalizedSpan = angleSpan % FULL_ELLIPSE_ANGLE;
	return normalizedSpan <= FULL_ELLIPSE_EPSILON
		|| Math.abs(normalizedSpan - FULL_ELLIPSE_ANGLE) <= FULL_ELLIPSE_EPSILON;
}

/**
 * 草图实体引用源 resolver。
 * 说明：
 * 1. 当前 resolver 已解析 line / circle / arc / ellipse / ellipse-arc / parabola / spline / style-spline 草图源；
 * 2. sketch-entity 会从运行时对象中提取完整平面几何快照；
 * 3. feature-edge 保留旧 descriptor 兼容路径，topology-edge 是模型/特征来源的正式 P1 路径；
 * 4. 该类只负责“来源解析”，不负责投影到目标草图平面。
 */
export class SketchReferenceSourceResolver {
	/** topology 边重定位服务。 */
	private readonly topologyEdgeRelocationService: TopologyEdgeRelocationService;
	/** 选择与重建共用的几何解析模块，内部统一选择精确或采样路径。 */
	private readonly selectionRuntimeSourceResolver: TopologySelectionRuntimeSourceResolver;
	/** 转换实体普通样条精确定义解析器。 */
	private readonly splineEvaluationDefinitionResolver =
		new SketchSplineReferenceEvaluationDefinitionResolver();

	/**
	 * 构造 source resolver。
	 *
	 * @param dependencies 可替换依赖，测试可按需注入。
	 */
	public constructor(dependencies: SketchReferenceSourceResolverDependencies = {}) {
		this.topologyEdgeRelocationService = dependencies.topologyEdgeRelocationService
			?? new TopologyEdgeRelocationService();
		this.selectionRuntimeSourceResolver = new TopologySelectionRuntimeSourceResolver({
			sampledCurveFittingService: dependencies.sampledCurveFittingService ?? new TopologySampledCurveFittingService(),
		});
	}

	/**
	 * 批量解析来源描述。
	 *
	 * @param input 解析输入。
	 * @returns 成功解析的几何快照及诊断。
	 */
	public resolve(
		input: SketchReferenceSourceResolveInput,
	): SketchReferenceSourceResolverResult {
		const resolvedSources: ResolvedSketchReferenceSource[] = [];
		const diagnostics: ExecutionDiagnostic[] = [];

		input.descriptors.forEach((descriptor) => {
			const runtimeTopologySource = descriptor.kind === 'topology-edge'
				? input.runtimeTopologySourcesBySourceKey?.[
					resolveExternalGeometrySourceDescriptorKey(descriptor)
				]
				: undefined;
			const resolution = this.resolveDescriptorSource(
				descriptor,
				input.runtime,
				runtimeTopologySource,
			);
			if (resolution.resolvedSource) {
				resolvedSources.push(resolution.resolvedSource);
			}
			diagnostics.push(...resolution.diagnostics);
		});

		return {
			resolvedSources,
			diagnostics,
		};
	}

	/**
	 * 把单条精确 topology 直边写入调用方复用向量。
	 *
	 * 该入口供镜像轴等鼠标热路径复用 Convert Entities 的稳定 owner/relocation 语义；常态路径不创建
	 * descriptor 数组、解析结果数组、诊断数组或世界坐标元组，身份漂移时才进入完整重定位慢路径。
	 *
	 * @param descriptor 可精确重解析的 topology 直边描述。
	 * @param runtime 当前运行时 owner 解析端口。
	 * @param startWorld 承载当前世界起点的复用向量。
	 * @param endWorld 承载当前世界终点的复用向量。
	 * @returns 唯一命中且端点有效时返回 true。
	 */
	public resolveExactTopologyLineTo(
		descriptor: TopologyEdgeSourceDescriptor,
		runtime: SketchReferenceSourceResolverRuntime,
		startWorld: THREE.Vector3,
		endWorld: THREE.Vector3,
	): boolean {
		if (
			descriptor.locator.curveKind !== 'line'
			|| descriptor.locator.resolutionLevel !== 'exact'
		) {
			return false;
		}

		if (typeof runtime.resolveTopologyOwnerHost === 'function') {
			const ownerHost = runtime.resolveTopologyOwnerHost(descriptor);
			if (!ownerHost) {
				return false;
			}
			const relocatedEdge = this.topologyEdgeRelocationService
				.relocateExactLineCandidateByDescriptor(descriptor, ownerHost);
			if (relocatedEdge?.featureLine?.copyWorldEndpointsTo(startWorld, endWorld)) {
				return true;
			}

			/*
			 * checkpoint 等统一 owner 不要求存在渲染代理，权威 topology 顶点会直接提供 worldLine。
			 * 热路径只读取元组并写入调用方复用向量，避免为镜向轴每帧创建临时 Vector3。
			 */
			const worldLine = relocatedEdge?.worldLine;
			if (!worldLine) {
				return false;
			}
			const start = worldLine.start;
			const end = worldLine.end;
			if (
				!Number.isFinite(start[0])
				|| !Number.isFinite(start[1])
				|| !Number.isFinite(start[2])
				|| !Number.isFinite(end[0])
				|| !Number.isFinite(end[1])
				|| !Number.isFinite(end[2])
			) {
				return false;
			}

			const deltaX = end[0] - start[0];
			const deltaY = end[1] - start[1];
			const deltaZ = end[2] - start[2];
			if (
				deltaX * deltaX + deltaY * deltaY + deltaZ * deltaZ
				<= GEOMETRY_EPSILON * GEOMETRY_EPSILON
			) {
				return false;
			}

			startWorld.set(start[0], start[1], start[2]);
			endWorld.set(end[0], end[1], end[2]);
			return true;
		}

		const resultObjectId = descriptor.owner.resultObjectId;
		const featureLine = resultObjectId
			? runtime.getFeatureLine(resultObjectId, descriptor.locator.edgeIndex)
			: null;
		return featureLine?.copyWorldEndpointsTo(startWorld, endWorld) ?? false;
	}

	/**
	 * 按来源描述类型分派解析。
	 *
	 * @param descriptor 来源描述。
	 * @param runtime 运行时依赖。
	 * @param runtimeTopologySource 当前选择已经确认的纯数据 topology 快照。
	 * @returns 单条解析结果。
	 */
	private resolveDescriptorSource(
		descriptor: ExternalGeometrySourceDescriptor,
		runtime: SketchReferenceSourceResolverRuntime,
		runtimeTopologySource?: TopologySelectionRuntimeSourceSnapshot,
	): {
		resolvedSource: ResolvedSketchReferenceSource | null;
		diagnostics: ExecutionDiagnostic[];
	} {
		if (descriptor.kind === 'sketch-entity') {
			return this.resolveSketchEntitySource(descriptor, runtime);
		}

		if (descriptor.kind === 'feature-edge') {
			return this.resolveFeatureEdgeSource(descriptor, runtime);
		}

		return this.resolveTopologyEdgeSource(
			descriptor,
			runtime,
			runtimeTopologySource,
		);
	}

	/**
	 * 解析草图内来源。
	 *
	 * @param descriptor 草图实体来源描述。
	 * @param runtime 运行时依赖。
	 * @returns 单条解析结果。
	 */
	private resolveSketchEntitySource(
		descriptor: Extract<ExternalGeometrySourceDescriptor, { kind: 'sketch-entity' }>,
		runtime: SketchReferenceSourceResolverRuntime,
	): {
		resolvedSource: ResolvedSketchReferenceSource | null;
		diagnostics: ExecutionDiagnostic[];
	} {
		const sourceObject = runtime.getSketchObject(descriptor.sourceSketchId, descriptor.entityId);
		if (!sourceObject) {
			return {
				resolvedSource: null,
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'input-invalid',
						level: 'error',
						message: `未找到来源草图实体: ${descriptor.sourceSketchId}/${descriptor.entityId}`,
						sourceRefEntityId: descriptor.entityId,
					}),
				],
			};
		}

		if (descriptor.subEntity?.kind === 'slot-edge') {
			return this.resolveSketchSlotEdgeSource(descriptor, sourceObject);
		}

		if (isSketchLineObject(sourceObject)) {
			return {
				resolvedSource: this.resolveSketchLineSource(descriptor, sourceObject),
				diagnostics: [],
			};
		}

		if (isSketchCircleObject(sourceObject)) {
			return {
				resolvedSource: this.resolveSketchCircleSource(descriptor, sourceObject),
				diagnostics: [],
			};
		}

		if (isSketchArcObject(sourceObject)) {
			return {
				resolvedSource: this.resolveSketchArcSource(descriptor, sourceObject),
				diagnostics: [],
			};
		}

		if (isSketchEllipseObject(sourceObject)) {
			return {
				resolvedSource: this.resolveSketchEllipseSource(descriptor, sourceObject),
				diagnostics: [],
			};
		}

		if (isSketchParabolaObject(sourceObject)) {
			return {
				resolvedSource: this.resolveSketchParabolaSource(descriptor, sourceObject),
				diagnostics: [],
			};
		}

		if (isSketchSplineObject(sourceObject)) {
			const resolvedSource = this.resolveSketchSplineSource(descriptor, sourceObject);
			if (resolvedSource.curveMetadata.curveKind === 'spline'
				&& !resolvedSource.curveEvaluationDefinition) {
				return {
					resolvedSource: null,
					diagnostics: [
						createExecutionDiagnostic({
							kind: 'unsupported-source',
							level: 'error',
							message: `普通插值样条尚未生成 Puppy CAD 内核精确求值定义: ${descriptor.entityId}`,
							sourceRefEntityId: descriptor.entityId,
						}),
					],
				};
			}
			return {
				resolvedSource,
				diagnostics: [],
			};
		}

		if (isSketchStyleSplineObject(sourceObject)) {
			return {
				resolvedSource: this.resolveSketchStyleSplineSource(descriptor, sourceObject),
				diagnostics: [],
			};
		}

		return {
			resolvedSource: null,
			diagnostics: [
				createExecutionDiagnostic({
					kind: 'unsupported-source',
					level: 'error',
					message: `当前 preview 暂不支持该草图来源类型: ${descriptor.entityId}`,
					sourceRefEntityId: descriptor.entityId,
				}),
			],
		};
	}

	/**
	 * 解析槽口内部边来源。
	 * 说明：
	 * 1. 槽口整体可被一次选择，但 Convert Entities 的重建单元必须是内部边；
	 * 2. descriptor 记录 slot-edge 拓扑序号，rebuild 时可以稳定回到同一条槽口边；
	 * 3. 槽口内部当前只包含 line / arc 两类边，直接复用既有解析逻辑。
	 *
	 * @param descriptor 槽口边来源描述。
	 * @param sourceObject 运行时解析出的槽口对象。
	 * @returns 单条槽口边解析结果。
	 */
	private resolveSketchSlotEdgeSource(
		descriptor: Extract<ExternalGeometrySourceDescriptor, { kind: 'sketch-entity' }>,
		sourceObject: Object3D,
	): {
		resolvedSource: ResolvedSketchReferenceSource | null;
		diagnostics: ExecutionDiagnostic[];
	} {
		if (!isSketchSlotObject(sourceObject)) {
			return {
				resolvedSource: null,
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'input-invalid',
						level: 'error',
						message: `槽口边来源指向的对象不是槽口: ${descriptor.sourceSketchId}/${descriptor.entityId}`,
						sourceRefEntityId: descriptor.entityId,
					}),
				],
			};
		}

		const edgeIndex = descriptor.subEntity?.edgeIndex ?? -1;
		const sourceEdge = resolveSketchSlotEdgeByIndex(sourceObject, edgeIndex);
		if (!sourceEdge) {
			return {
				resolvedSource: null,
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'input-invalid',
						level: 'error',
						message: `槽口边来源索引无效: ${descriptor.sourceSketchId}/${descriptor.entityId}/${edgeIndex}`,
						sourceRefEntityId: descriptor.entityId,
					}),
				],
			};
		}

		if (isSketchLineObject(sourceEdge)) {
			return {
				resolvedSource: this.resolveSketchLineSource(descriptor, sourceEdge),
				diagnostics: [],
			};
		}

		return {
			resolvedSource: this.resolveSketchArcSource(descriptor, sourceEdge),
			diagnostics: [],
		};
	}

	/**
	 * 解析 topology 边来源。
	 *
	 * 说明：
	 * 1. exact line 会读取当前运行时 FeatureLine，目标草图实体由当前投影结果决定；
	 * 2. 带 fitMetadata 的 fitted-exact 是 P2-B 已拟合来源，后续仍需回到当前采样点重拟合；
	 * 3. sampled-only 不承诺无损转换，但 P2-B 会尝试生成解析曲线或普通 spline 近似；
	 * 4. unsupported 或缺少线型几何载荷时显式报错，避免被误当作旧 feature-edge 解析。
	 *
	 * @param descriptor topology 边来源描述。
	 * @param runtime 运行时依赖。
	 * @param runtimeTopologySource 当前选择已经确认的纯数据 topology 快照。
	 * @returns 单条解析结果。
	 */
	private resolveTopologyEdgeSource(
		descriptor: Extract<ExternalGeometrySourceDescriptor, { kind: 'topology-edge' }>,
		runtime: SketchReferenceSourceResolverRuntime,
		runtimeTopologySource?: TopologySelectionRuntimeSourceSnapshot,
	): {
		resolvedSource: ResolvedSketchReferenceSource | null;
		diagnostics: ExecutionDiagnostic[];
	} {
		const sourceRefEntityId = resolveExternalGeometrySourceRefEntityId(descriptor);
		if (this.shouldResolveTopologyCurveSource(descriptor)) {
			return this.resolveTopologyCurveSource({
				descriptor,
				runtime,
				sourceRefEntityId,
				runtimeTopologySource,
			});
		}

		if (descriptor.locator.resolutionLevel === 'fitted-exact') {
			return {
				resolvedSource: null,
				diagnostics: [
					createDescriptorInsufficientDiagnostic(
						descriptor,
						`topology-edge fitted-exact 缺少 P2-B fitMetadata，不能安全重拟合: ${sourceRefEntityId}`,
					),
				],
			};
		}

		if (descriptor.locator.resolutionLevel === 'unsupported') {
			return {
				resolvedSource: null,
				diagnostics: [
					createDescriptorInsufficientDiagnostic(
						descriptor,
						`topology-edge 当前曲线类型不支持转换实体引用: ${sourceRefEntityId}`,
					),
				],
			};
		}

		const fingerprint = descriptor.locator.geomFingerprint;
		if (
			descriptor.locator.curveKind !== 'line'
			|| fingerprint?.kind !== 'line'
		) {
			return {
				resolvedSource: null,
				diagnostics: [
					createDescriptorInsufficientDiagnostic(
						descriptor,
						`topology-edge 缺少可精确解析的线型几何载荷: ${sourceRefEntityId}`,
					),
				],
			};
		}

		if (runtimeTopologySource) {
			return this.selectionRuntimeSourceResolver.resolve({
				descriptor,
				runtimeSource: runtimeTopologySource,
			});
		}

		if (typeof runtime.resolveTopologyOwnerHost === 'function') {
			const ownerHost = runtime.resolveTopologyOwnerHost(descriptor);
			if (!ownerHost) {
				return {
					resolvedSource: null,
					diagnostics: [
						createExecutionDiagnostic({
							kind: 'source-missing',
							level: 'warning',
							message: `topology-edge 当前 owner 未命中，不能回退到旧 resultObjectId 边代理: ${sourceRefEntityId}`,
							sourceRefEntityId,
						}),
					],
				};
			}

			const relocationResult = this.topologyEdgeRelocationService.relocate({
				descriptor,
				host: ownerHost,
			});
			if (relocationResult.status === 'failed') {
				return {
					resolvedSource: null,
					diagnostics: [relocationResult.diagnostic],
				};
			}

			const featureLine = relocationResult.edge.featureLine ?? null;
			if (relocationResult.edge.worldLine || !featureLine) {
				if (relocationResult.edge.worldLine) {
					/*
					 * checkpoint host 没有 FeatureLine，但 worldLine 同样来自当前重放快照。
					 * 必须与 Scene 路径一样回写当前 locator，否则无语义兼容命中后仍会
					 * 保存旧身份，下一轮无法迁移到当前 semanticName。
					 */
					const currentDescriptor = this.createRelocatedTopologyEdgeDescriptor({
						descriptor,
						relocatedEdge: relocationResult.edge,
						startWorld: new THREE.Vector3(...relocationResult.edge.worldLine.start),
						endWorld: new THREE.Vector3(...relocationResult.edge.worldLine.end),
					});
					return {
						resolvedSource: this.attachTopologyEndpointAnchorIds(
							{
								sourceKind: 'line',
								sourceDescriptor: currentDescriptor,
								diagnostics: [],
								startWorld: [...relocationResult.edge.worldLine.start],
								endWorld: [...relocationResult.edge.worldLine.end],
							},
							currentDescriptor,
							relocationResult.edge,
						),
						diagnostics: [],
					};
				}
				return {
					resolvedSource: null,
					diagnostics: [
						createExecutionDiagnostic({
							kind: 'source-missing',
							level: 'warning',
							message: `topology-edge 已重定位到当前 owner，但缺少可读取的 FeatureLine 代理: ${sourceRefEntityId}`,
							sourceRefEntityId,
						}),
					],
				};
			}

			return this.resolveTopologyFeatureLineSource({
				descriptor,
				relocatedEdge: relocationResult.edge,
				featureLine,
				sourceRefEntityId,
			});
		}

		/**
		 * P1 兼容路径：resultObjectId 只是当前会话的运行时对象 ID，
		 * 只能用于未接入 owner host 时的快速回绑，不能承担跨重建的稳定来源定位。
		 */
		// P1 兼容路径只服务于未接入 owner resolver 的旧 runtime，避免 P2-A owner 缺失时误用过期边代理。
		const resultObjectId = descriptor.owner.resultObjectId;
		if (!resultObjectId) {
			return {
				resolvedSource: null,
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'feature-edge-missing',
						level: 'error',
						message: `topology-edge 缺少运行时 resultObjectId，无法读取当前边代理: ${sourceRefEntityId}`,
						sourceRefEntityId,
					}),
				],
			};
		}

		const featureLine = runtime.getFeatureLine(resultObjectId, descriptor.locator.edgeIndex);
		if (!featureLine) {
			return {
				resolvedSource: null,
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'feature-edge-missing',
						level: 'error',
						message: `未找到 topology-edge 运行时边代理: ${resultObjectId}/${descriptor.locator.edgeIndex}`,
						sourceRefEntityId,
					}),
				],
			};
		}

		return this.resolveTopologyFeatureLineSource({
			descriptor,
			featureLine,
			sourceRefEntityId,
		});
	}

	/**
	 * 判断 topology-edge 是否需要曲线解析，而不是直线端点解析。
	 *
	 * @param descriptor topology 边来源描述。
	 * @returns 精确曲线或可恢复采样来源返回 true，具体优先级由共用解析器裁决。
	 */
	private shouldResolveTopologyCurveSource(
		descriptor: TopologyEdgeSourceDescriptor,
	): boolean {
		return descriptor.locator.resolutionLevel === 'sampled-only'
			|| (descriptor.locator.resolutionLevel === 'exact' && descriptor.locator.curveKind !== 'line')
			|| (
				descriptor.locator.resolutionLevel === 'fitted-exact'
				&& Boolean(descriptor.locator.fitMetadata)
			);
	}

	/**
	 * 重定位 topology 曲线来源，再交给选择/重建共用的几何解析器。
	 *
	 * 本层只负责身份定位，不重复实现精确曲线读取或采样拟合。
	 *
	 * @param input 曲线来源描述、runtime 与诊断上下文。
	 * @returns 单条解析结果。
	 */
	private resolveTopologyCurveSource(input: {
		descriptor: TopologyEdgeSourceDescriptor;
		runtime: SketchReferenceSourceResolverRuntime;
		sourceRefEntityId: string;
		runtimeTopologySource?: TopologySelectionRuntimeSourceSnapshot;
	}): {
		resolvedSource: ResolvedSketchReferenceSource | null;
		diagnostics: ExecutionDiagnostic[];
	} {
		const {descriptor, runtime, sourceRefEntityId} = input;
		if (input.runtimeTopologySource) {
			return this.selectionRuntimeSourceResolver.resolve({
				descriptor,
				runtimeSource: input.runtimeTopologySource,
			});
		}
		if (typeof runtime.resolveTopologyOwnerHost !== 'function') {
			return {
				resolvedSource: null,
				diagnostics: [
					createDescriptorInsufficientDiagnostic(
						descriptor,
						`topology-edge 曲线解析需要当前 topology owner host: ${sourceRefEntityId}`,
					),
				],
			};
		}

		const ownerHost = runtime.resolveTopologyOwnerHost(descriptor);
		if (!ownerHost) {
			return {
				resolvedSource: null,
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'source-missing',
						level: 'warning',
						message: `topology-edge 当前 owner 未命中，无法读取当前曲线几何: ${sourceRefEntityId}`,
						sourceRefEntityId,
					}),
				],
			};
		}

		const relocationResult = this.topologyEdgeRelocationService.relocate({
			descriptor,
			host: ownerHost,
		});
		if (relocationResult.status === 'failed') {
			return {
				resolvedSource: null,
				diagnostics: [relocationResult.diagnostic],
			};
		}

		const currentDescriptor = this.createRelocatedTopologyEdgeIdentityDescriptor({
			descriptor,
			relocatedEdge: relocationResult.edge,
		});
		// 首次选择与重建复用同一优先级：精确参数在前，仅采样来源才拟合。
		return this.selectionRuntimeSourceResolver.resolve({
			descriptor: currentDescriptor,
			runtimeSource: relocationResult.edge,
		});
	}

	/**
	 * 将 topology FeatureLine 代理解析为 line 来源。
	 *
	 * 说明：owner-host 重定位已经负责身份确认，这里只读取当前端点。
	 * 特征尺寸重建会改变端点坐标，不能再用创建时的旧几何指纹否定当前拓扑边。
	 *
	 * @param input FeatureLine 与 descriptor 校验上下文。
	 * @returns 单条 topology line 来源解析结果。
	 */
	/**
	 * 为 topology 来源附加端点锚点元数据。
	 *
	 * @param source 已解析的来源几何。
	 * @param descriptor topology 边来源描述。
	 * @param relocatedEdge 当前重定位命中的 topology 边候选。
	 * @returns 携带端点锚点的来源几何。
	 */
	private attachTopologyEndpointAnchorIds<T extends ResolvedSketchReferenceSource>(
		source: T,
		descriptor: TopologyEdgeSourceDescriptor,
		relocatedEdge?: TopologyEdgeRelocationCandidate,
	): T {
		const endpointAnchorIds = this.resolveTopologyEndpointAnchorIds(descriptor, relocatedEdge);
		if (!endpointAnchorIds) {
			return source;
		}

		return {
			...source,
			endpointAnchorIds,
		} as T;
	}

	/**
	 * 解析 topology 边端点的稳定锚点 ID。
	 *
	 * @param descriptor topology 边来源描述。
	 * @param relocatedEdge 当前重定位命中的 topology 边候选。
	 * @returns 可用于投影结果的端点锚点 ID；缺少稳定顶点身份时返回 null。
	 */
	private resolveTopologyEndpointAnchorIds(
		descriptor: TopologyEdgeSourceDescriptor,
		relocatedEdge?: TopologyEdgeRelocationCandidate,
	): ResolvedReferenceSourceEndpointAnchorIds | null {
		const endpointAnchorRefs = relocatedEdge?.endpointAnchorRefs;
		if (!endpointAnchorRefs?.start && !endpointAnchorRefs?.end) {
			return null;
		}

		return {
			start: endpointAnchorRefs.start
				? resolveTopologyEndpointAnchorId(descriptor.owner, endpointAnchorRefs.start)
				: undefined,
			end: endpointAnchorRefs.end
				? resolveTopologyEndpointAnchorId(descriptor.owner, endpointAnchorRefs.end)
				: undefined,
		};
	}

	private resolveTopologyFeatureLineSource(input: {
		descriptor: Extract<ExternalGeometrySourceDescriptor, { kind: 'topology-edge' }>;
		relocatedEdge?: TopologyEdgeRelocationCandidate;
		featureLine: FeatureLine;
		sourceRefEntityId: string;
	}): {
		resolvedSource: ResolvedSketchReferenceSource | null;
		diagnostics: ExecutionDiagnostic[];
	} {
		const endpoints = input.featureLine.getWorldEndpoints();
		if (!endpoints) {
			return {
				resolvedSource: null,
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'feature-edge-missing',
						level: 'error',
						message: `topology-edge 运行时边代理缺少有效端点: ${input.sourceRefEntityId}`,
						sourceRefEntityId: input.sourceRefEntityId,
					}),
				],
			};
		}

		const currentDescriptor = input.relocatedEdge
			? this.createRelocatedTopologyEdgeDescriptor({
				descriptor: input.descriptor,
				relocatedEdge: input.relocatedEdge,
				startWorld: endpoints.start,
				endWorld: endpoints.end,
			})
			: input.descriptor;

		return {
			resolvedSource: this.attachTopologyEndpointAnchorIds(
				{
					sourceKind: 'line',
					sourceDescriptor: currentDescriptor,
					diagnostics: [],
					startWorld: toWorldCoordinate(endpoints.start),
					endWorld: toWorldCoordinate(endpoints.end),
				},
				currentDescriptor,
				input.relocatedEdge,
			),
			diagnostics: [],
		};
	}

	/**
	 * 根据当前重定位候选刷新 topology-edge 来源描述。
	 *
	 * 设计说明：
	 * 1. 重建成功后应把当前 topology 身份写回引用记录，避免后续每次都依赖旧 index 兜底；
	 * 2. `owner` 仍保留原始 feature/model owner，不在 resolver 中猜测 body 绑定策略；
	 * 3. 当前候选提供的新身份优先，缺失的可选物理字段沿用旧记录；
	 * 4. 相邻面提示和几何指纹属于当前快照，必须替换旧值，避免过期提示参与下一次重定位。
	 *
	 * @param input 原 descriptor、当前候选边与当前端点。
	 * @returns 刷新后的 topology-edge descriptor。
	 */
	private createRelocatedTopologyEdgeDescriptor(input: {
		descriptor: TopologyEdgeSourceDescriptor;
		relocatedEdge: TopologyEdgeRelocationCandidate;
		startWorld: THREE.Vector3;
		endWorld: THREE.Vector3;
	}): TopologyEdgeSourceDescriptor {
		const {descriptor, relocatedEdge, startWorld, endWorld} = input;
		const matchHints = this.createTopologyEdgeMatchHintsFromRelocatedEdge(relocatedEdge);
		return {
			...descriptor,
			locator: {
				...descriptor.locator,
				edgeIndex: relocatedEdge.edgeIndex,
				semanticName: relocatedEdge.semanticName ?? descriptor.locator.semanticName,
				persistentRef: relocatedEdge.persistentRef ?? descriptor.locator.persistentRef,
				subEntityId: relocatedEdge.subEntityId ?? descriptor.locator.subEntityId,
				matchHints,
				geomFingerprint: {
					kind: 'line',
					startWorld: toWorldCoordinate(startWorld),
					endWorld: toWorldCoordinate(endWorld),
				},
			},
		};
	}

	/**
	 * 根据当前重定位候选刷新 topology-edge 身份字段。
	 *
	 * @param input 原 descriptor 与当前候选边。
	 * @returns 只刷新身份和相邻面提示的 descriptor。
	 */
	private createRelocatedTopologyEdgeIdentityDescriptor(input: {
		descriptor: TopologyEdgeSourceDescriptor;
		relocatedEdge: TopologyEdgeRelocationCandidate;
	}): TopologyEdgeSourceDescriptor {
		const {descriptor, relocatedEdge} = input;
		const matchHints = this.createTopologyEdgeMatchHintsFromRelocatedEdge(relocatedEdge);
		return {
			...descriptor,
			// 重定位已确认物理边迁移时，成员身份必须与同批几何一起更新。
			owner: relocatedEdge.topologyMemberId
				? {...descriptor.owner, resultObjectId: relocatedEdge.topologyMemberId}
				: descriptor.owner,
			locator: {
				...descriptor.locator,
				edgeIndex: relocatedEdge.edgeIndex,
				semanticName: relocatedEdge.semanticName ?? descriptor.locator.semanticName,
				persistentRef: relocatedEdge.persistentRef ?? descriptor.locator.persistentRef,
				subEntityId: relocatedEdge.subEntityId ?? descriptor.locator.subEntityId,
				matchHints,
			},
		};
	}

	/**
	 * 从当前候选边提取相邻面匹配提示。
	 *
	 * @param relocatedEdge 当前重定位候选边。
	 * @returns 当前候选携带的相邻面提示；没有有效提示时返回 undefined。
	 */
	private createTopologyEdgeMatchHintsFromRelocatedEdge(
		relocatedEdge: TopologyEdgeRelocationCandidate,
	): TopologyEdgeMatchHints | undefined {
		const adjacentFaceRefs = compactUniqueStrings(relocatedEdge.owningFaceRefs ?? []);
		const adjacentFaceSubEntityIds = compactUniqueStrings(relocatedEdge.adjacentFaceSubEntityIds ?? []);
		if (adjacentFaceRefs.length <= 0 && adjacentFaceSubEntityIds.length <= 0) {
			return undefined;
		}

		return {
			adjacentFaceRefs: adjacentFaceRefs.length > 0 ? adjacentFaceRefs : undefined,
			adjacentFaceSubEntityIds: adjacentFaceSubEntityIds.length > 0 ? adjacentFaceSubEntityIds : undefined,
		};
	}

	/**
	 * 解析特征边来源。
	 *
	 * @param descriptor 特征边来源描述。
	 * @param runtime 运行时依赖。
	 * @returns 单条解析结果。
	 */
	private resolveFeatureEdgeSource(
		descriptor: Extract<ExternalGeometrySourceDescriptor, { kind: 'feature-edge' }>,
		runtime: SketchReferenceSourceResolverRuntime,
	): {
		resolvedSource: ResolvedSketchReferenceSource | null;
		diagnostics: ExecutionDiagnostic[];
	} {
		const { geomFingerprint } = descriptor.locator;
		if (geomFingerprint.kind !== 'line') {
			return {
				resolvedSource: null,
				diagnostics: [
					createDescriptorInsufficientDiagnostic(
						descriptor,
						geomFingerprint.kind === 'circle'
							? `feature-edge circle 在当前 descriptor 中缺少法向/局部轴信息，无法安全生成 preview 投影: ${descriptor.locator.owningObjectId}/${descriptor.locator.featureEdgeIndex}`
							: `feature-edge arc 在当前 descriptor 中缺少法向/局部轴/方向信息，无法安全生成 preview 投影: ${descriptor.locator.owningObjectId}/${descriptor.locator.featureEdgeIndex}`,
					),
				],
			};
		}

		const featureLine = runtime.getFeatureLine(
			descriptor.locator.owningObjectId,
			descriptor.locator.featureEdgeIndex,
		);
		if (!featureLine) {
			return {
				resolvedSource: null,
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'feature-edge-missing',
						level: 'error',
						message: `未找到特征边来源: ${descriptor.locator.owningObjectId}/${descriptor.locator.featureEdgeIndex}`,
						sourceRefEntityId: `${descriptor.locator.owningObjectId}/${descriptor.locator.featureEdgeIndex}`,
					}),
				],
			};
		}

		const endpoints = featureLine.getWorldEndpoints();
		if (!endpoints) {
			return {
				resolvedSource: null,
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'feature-edge-missing',
						level: 'error',
						message: `特征边几何缺少有效端点: ${descriptor.locator.owningObjectId}/${descriptor.locator.featureEdgeIndex}`,
						sourceRefEntityId: `${descriptor.locator.owningObjectId}/${descriptor.locator.featureEdgeIndex}`,
					}),
				],
			};
		}

		if (!isMatchingLineFingerprint(geomFingerprint, endpoints.start, endpoints.end)) {
			return {
				resolvedSource: null,
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'feature-edge-missing',
						level: 'error',
						message: `feature-edge line 指纹与当前几何不一致，无法安全生成 preview: ${descriptor.locator.owningObjectId}/${descriptor.locator.featureEdgeIndex}`,
						sourceRefEntityId: `${descriptor.locator.owningObjectId}/${descriptor.locator.featureEdgeIndex}`,
					}),
				],
			};
		}

		return {
			resolvedSource: {
				sourceKind: 'line',
				sourceDescriptor: descriptor,
				diagnostics: [],
				startWorld: toWorldCoordinate(endpoints.start),
				endWorld: toWorldCoordinate(endpoints.end),
			},
			diagnostics: [],
		};
	}

	/**
	 * 解析草图线来源。
	 *
	 * @param descriptor 来源描述。
	 * @param sourceObject 草图线对象。
	 * @returns 解析后的线几何快照。
	 */
	private resolveSketchLineSource(
		descriptor: Extract<ExternalGeometrySourceDescriptor, { kind: 'sketch-entity' }>,
		sourceObject: SketchLineObject,
	): ResolvedLineReferenceSource {
		return {
			sourceKind: 'line',
			sourceDescriptor: descriptor,
			diagnostics: [],
			startWorld: toWorldCoordinate(sourceObject.startPoint.getPosition()),
			endWorld: toWorldCoordinate(sourceObject.endPoint.getPosition()),
		};
	}

	/**
	 * 解析草图圆来源。
	 *
	 * @param descriptor 来源描述。
	 * @param sourceObject 草图圆对象。
	 * @returns 解析后的圆几何快照。
	 */
	private resolveSketchCircleSource(
		descriptor: Extract<ExternalGeometrySourceDescriptor, { kind: 'sketch-entity' }>,
		sourceObject: SketchCircleObject,
	): ResolvedCircleReferenceSource {
		return {
			sourceKind: 'circle',
			sourceDescriptor: descriptor,
			diagnostics: [],
			centerWorld: toWorldCoordinate(sourceObject.getCenterWorld()),
			radius: sourceObject.getRadius(),
			planeXAxisWorld: toWorldCoordinate(sourceObject.referencePlane.xAxis),
			planeYAxisWorld: toWorldCoordinate(sourceObject.referencePlane.yAxis),
		};
	}

	/**
	 * 解析草图圆弧来源。
	 *
	 * @param descriptor 来源描述。
	 * @param sourceObject 草图圆弧对象。
	 * @returns 解析后的圆弧几何快照。
	 */
	private resolveSketchArcSource(
		descriptor: Extract<ExternalGeometrySourceDescriptor, { kind: 'sketch-entity' }>,
		sourceObject: SketchArcObject,
	): ResolvedArcReferenceSource {
		const arcDefinition = sourceObject.getArcDefinition();
		return {
			sourceKind: 'arc',
			sourceDescriptor: descriptor,
			diagnostics: [],
			centerWorld: toWorldCoordinate(arcDefinition.centerWorld),
			radius: arcDefinition.radius,
			planeXAxisWorld: toWorldCoordinate(sourceObject.referencePlane.xAxis),
			planeYAxisWorld: toWorldCoordinate(sourceObject.referencePlane.yAxis),
			startAngle: arcDefinition.startAngle,
			endAngle: arcDefinition.endAngle,
			clockwise: arcDefinition.clockwise,
		};
	}

	/**
	 * 解析草图椭圆/椭圆弧来源。
	 *
	 * @param descriptor 来源描述。
	 * @param sourceObject 草图椭圆对象。
	 * @returns 解析后的椭圆或椭圆弧几何快照。
	 */
	private resolveSketchEllipseSource(
		descriptor: Extract<ExternalGeometrySourceDescriptor, { kind: 'sketch-entity' }>,
		sourceObject: SketchEllipseObject,
	): ResolvedEllipseReferenceSource | ResolvedEllipseArcReferenceSource {
		const ellipseDefinition = sourceObject.getEllipseDefinition();
		const commonSnapshot = {
			sourceDescriptor: descriptor,
			diagnostics: [],
			centerWorld: toWorldCoordinate(ellipseDefinition.centerWorld),
			radiusX: ellipseDefinition.radiusX,
			radiusY: ellipseDefinition.radiusY,
			planeXAxisWorld: toWorldCoordinate(sourceObject.referencePlane.xAxis),
			planeYAxisWorld: toWorldCoordinate(sourceObject.referencePlane.yAxis),
			rotation: ellipseDefinition.rotation,
		};

		if (isFullEllipseDefinition(ellipseDefinition)) {
			return {
				...commonSnapshot,
				sourceKind: 'ellipse',
			};
		}

		return {
			...commonSnapshot,
			sourceKind: 'ellipse-arc',
			startAngle: ellipseDefinition.startAngle,
			endAngle: ellipseDefinition.endAngle,
			clockwise: ellipseDefinition.clockwise,
		};
	}

	/**
	 * 解析草图抛物线来源。
	 *
	 * @param descriptor 来源描述。
	 * @param sourceObject 草图抛物线对象。
	 * @returns 抛物线几何快照。
	 */
	private resolveSketchParabolaSource(
		descriptor: Extract<ExternalGeometrySourceDescriptor, { kind: 'sketch-entity' }>,
		sourceObject: SketchParabolaObject,
	): ResolvedParabolaReferenceSource {
		const parabolaDefinition = sourceObject.getParabolaDefinition();
		return {
			sourceKind: 'parabola',
			sourceDescriptor: descriptor,
			diagnostics: [],
			focalWorld: toWorldCoordinate(parabolaDefinition.focalWorld),
			apexWorld: toWorldCoordinate(parabolaDefinition.apexWorld),
			startParameter: parabolaDefinition.startParameter,
			endParameter: parabolaDefinition.endParameter,
			segments: parabolaDefinition.segments,
			curvePointWorlds: sourceObject.getCurvePoints().map((point) => toWorldCoordinate(point)),
		};
	}

	/**
	 * 解析普通样条来源。
	 *
	 * @param descriptor 来源描述。
	 * @param sourceObject 普通样条对象。
	 * @returns 普通样条几何快照。
	 */
	private resolveSketchSplineSource(
		descriptor: Extract<ExternalGeometrySourceDescriptor, { kind: 'sketch-entity' }>,
		sourceObject: SketchSplineObject,
	): ResolvedSplineReferenceSource {
		const controlPointPositions = sourceObject.getControlPoints()
			.map((point) => point.getPosition());
		const curveMetadata = sourceObject.getCurveMetadata();
		const curveEvaluationDefinition = this.splineEvaluationDefinitionResolver.resolve(sourceObject);

		// 旧文档可能没有持久化 Worker 定义；只允许在数学上可无损重建的两点或闭合样条上补齐。
		return {
			sourceKind: 'spline',
			sourceDescriptor: descriptor,
			diagnostics: [],
			controlPointWorlds: controlPointPositions.map(toWorldCoordinate),
			curvePointWorlds: sourceObject.getCurvePoints()
				.map((point) => toWorldCoordinate(point)),
			curveMetadata,
			...(curveEvaluationDefinition
				? {curveEvaluationDefinition}
				: {}),
		};
	}

	/**
	 * 解析样式样条来源。
	 *
	 * @param descriptor 来源描述。
	 * @param sourceObject 样式样条对象。
	 * @returns 样式样条几何快照。
	 */
	private resolveSketchStyleSplineSource(
		descriptor: Extract<ExternalGeometrySourceDescriptor, { kind: 'sketch-entity' }>,
		sourceObject: SketchStyleSplineObject,
	): ResolvedStyleSplineReferenceSource {
		return {
			sourceKind: 'style-spline',
			sourceDescriptor: descriptor,
			diagnostics: [],
			controlPointWorlds: sourceObject.getControlPoints()
				.map((point) => toWorldCoordinate(point.getPosition())),
			curvePointWorlds: sourceObject.getCurvePoints()
				.map((point) => toWorldCoordinate(point)),
			curveMetadata: sourceObject.getCurveMetadata(),
		};
	}
}
